import React, { useState, useMemo } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { ModuleInfoModal } from '../components/ModuleInfoModal';
import {
    Shield,
    Eye,
    Search,
    Filter,
    Plus,
    MoreVertical,
    CheckCircle,
    Layers,
    Activity
} from 'lucide-react';

export const ControlsManager: React.FC = () => {
    const { theme, createControl, allData } = useAppContext();
    const [activeTab, setActiveTab] = useState<'all' | 'prevention' | 'detection'>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const controls = allData.controls; // Live data from backend/Firebase

    const handleCreateControl = async () => {
        await createControl({
            name: "New System Control",
            description: "Default control description for new entity",
            type: "prevention",
            effectiveness: "Medium",
            bomIds: []
        });
    };

    // Stats Logic
    const stats = useMemo(() => {
        const total = controls.length;
        const prevention = controls.filter(c => c.type === 'prevention').length;
        const detection = controls.filter(c => c.type === 'detection').length;
        const highEff = controls.filter(c => c.effectiveness === 'High').length;
        const highEffPercent = total > 0 ? Math.round((highEff / total) * 100) : 0;
        return { total, prevention, detection, highEffPercent };
    }, [controls]);

    // Filtering Logic
    const filteredControls = useMemo(() => {
        return controls.filter(c => {
            const matchesTab = activeTab === 'all' || c.type === activeTab;
            const q = searchQuery.toLowerCase();
            const matchesSearch =
                c.name.toLowerCase().includes(q) ||
                c.description?.toLowerCase().includes(q) ||
                c.id.toLowerCase().includes(q) ||
                c.keywords?.some(k => k.toLowerCase().includes(q));

            return matchesTab && matchesSearch;
        });
    }, [controls, activeTab, searchQuery]);

    const getEffectivenessColor = (eff?: string) => {
        if (eff === 'High') return 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20';
        if (eff === 'Medium') return 'text-amber-400 bg-amber-400/10 border-amber-400/20';
        return 'text-rose-400 bg-rose-400/10 border-rose-400/20';
    };

    return (
        <>
            {/* Background Gradients - Only in dark mode */}
            {theme === 'dark' && (
                <div className="fixed inset-0 pointer-events-none">
                    <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[100px]" />
                    <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[100px]" />
                </div>
            )}

            <div className="relative z-10 space-y-8">

                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className={`text-3xl font-bold ${theme === 'dark'
                            ? 'bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent'
                            : 'text-text-primary'
                            }`}>
                            Controls Library
                        </h1>
                        <p className={theme === 'dark' ? 'text-slate-400 mt-1' : 'text-text-secondary mt-1'}>
                            Manage reusable prevention and detection controls across projects.
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <ModuleInfoModal
                            title="Controls Library"
                            description="The Controls Library is a centralized repository for managing reusable prevention and detection controls across all your FMEA projects. It provides a standardized catalog of control measures that can be consistently applied to reduce failure risks."
                            description2="Prevention controls aim to eliminate or reduce the occurrence of failure modes, while detection controls help identify failures before they impact customers. By maintaining a library, you ensure consistency, save time, and improve the effectiveness of your risk mitigation strategies."
                            benefits={[
                                "Centralized repository of proven control measures for reuse across multiple projects",
                                "Standardized effectiveness ratings (High, Medium, Low) for each control",
                                "Easy search and filter capabilities to find the right control quickly",
                                "Track which BOM items are linked to each control for traceability",
                                "Maintain consistency in control descriptions and implementation across teams",
                                "Reduce time spent creating FMEAs by leveraging pre-defined controls"
                            ]}
                            usageInstructions={[
                                "Browse all controls or filter by type (Prevention / Detection) using the tab buttons",
                                "Use the search bar to find controls by name, description, ID, or keywords",
                                "Review the effectiveness rating (High/Medium/Low) to select the most impactful controls",
                                "Check 'Linked Items' to see how many BOM components use each control",
                                "Click 'New Control' to add custom controls specific to your project needs",
                                "Apply controls from this library when creating or editing FMEA items"
                            ]}
                        />
                        <button
                            onClick={handleCreateControl}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${theme === 'dark'
                                ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/20'
                                : 'bg-primary hover:bg-primary/90 text-white shadow-md'
                                }`}>
                            <Plus className="w-4 h-4" />
                            <span>New Control</span>
                        </button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <StatCard
                        title="Total Controls"
                        value={stats.total}
                        icon={Layers}
                        color="blue"
                        theme={theme}
                    />
                    <StatCard
                        title="Prevention"
                        value={stats.prevention}
                        icon={Shield}
                        color="emerald"
                        theme={theme}
                    />
                    <StatCard
                        title="Detection"
                        value={stats.detection}
                        icon={Eye}
                        color="purple"
                        theme={theme}
                    />
                    <div className={`p-4 rounded-xl border ${theme === 'dark'
                        ? 'border-slate-700/50 bg-slate-800/40 backdrop-blur-sm'
                        : 'border-border bg-card'
                        }`}>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 rounded-lg bg-amber-500/10 text-amber-500">
                                <Activity className="w-5 h-5" />
                            </div>
                            <span className={`text-sm font-medium ${theme === 'dark' ? 'text-slate-400' : 'text-text-secondary'}`}>Effectiveness</span>
                        </div>
                        <div className="flex items-baseline gap-2">
                            <span className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-text-primary'}`}>{stats.highEffPercent}%</span>
                            <span className={`text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-text-secondary'}`}>rated High</span>
                        </div>
                        <div className={`w-full h-1 rounded-full mt-3 overflow-hidden ${theme === 'dark' ? 'bg-slate-700' : 'bg-border'}`}>
                            <div
                                className="h-full bg-amber-500 transition-all duration-500"
                                style={{ width: `${stats.highEffPercent}%` }}
                            />
                        </div>
                    </div>
                </div>

                {/* Filters & Search */}
                <div className={`flex flex-col md:flex-row gap-4 items-center justify-between p-4 rounded-xl border ${theme === 'dark'
                    ? 'bg-slate-800/20 border-slate-700/30 backdrop-blur-sm'
                    : 'bg-card border-border'
                    }`}>
                    <div className={`flex items-center gap-1 p-1 rounded-lg border ${theme === 'dark'
                        ? 'bg-slate-900/50 border-slate-700/50'
                        : 'bg-background border-border'
                        }`}>
                        <TabButton
                            active={activeTab === 'all'}
                            onClick={() => setActiveTab('all')}
                            label="All Controls"
                            theme={theme}
                        />
                        <TabButton
                            active={activeTab === 'prevention'}
                            onClick={() => setActiveTab('prevention')}
                            label="Prevention"
                            icon={Shield}
                            theme={theme}
                        />
                        <TabButton
                            active={activeTab === 'detection'}
                            onClick={() => setActiveTab('detection')}
                            label="Detection"
                            icon={Eye}
                            theme={theme}
                        />
                    </div>

                    <div className="relative w-full md:w-96">
                        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${theme === 'dark' ? 'text-slate-500' : 'text-text-secondary'
                            }`} />
                        <input
                            type="text"
                            placeholder="Search controls..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className={`w-full rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 transition-all ${theme === 'dark'
                                ? 'bg-slate-900/50 border border-slate-700/50 text-slate-200 focus:ring-blue-500/50 focus:border-blue-500/50 placeholder:text-slate-600'
                                : 'bg-background border border-border text-text-primary focus:ring-primary/50 focus:border-primary placeholder:text-text-secondary/50'
                                }`}
                        />
                    </div>
                </div>

                {/* Controls Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredControls.map((control) => (
                        <div
                            key={control.id}
                            className={`group relative rounded-xl p-5 transition-all duration-300 border ${theme === 'dark'
                                ? 'bg-slate-800/40 hover:bg-slate-800/60 border-slate-700/50 hover:border-slate-600 hover:shadow-lg hover:shadow-black/20'
                                : 'bg-card hover:bg-card/80 border-border hover:border-primary/30 hover:shadow-md'
                                }`}
                        >
                            <div className="flex justify-between items-start mb-3">
                                <div className={`
                    p-2 rounded-lg border 
                    ${control.type === 'prevention'
                                        ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                                        : 'bg-purple-500/10 border-purple-500/20 text-purple-400'}
                  `}>
                                    {control.type === 'prevention' ? <Shield className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </div>
                                <div className={`text-xs px-2 py-1 rounded-full border ${getEffectivenessColor(control.effectiveness)}`}>
                                    {control.effectiveness || 'Unrated'}
                                </div>
                            </div>

                            <div className="mb-4">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className={`text-xs font-mono ${theme === 'dark' ? 'text-slate-500' : 'text-text-secondary'}`}>{control.id}</span>
                                    {control.status === 'active' && <CheckCircle className="w-3 h-3 text-emerald-500" />}
                                </div>
                                <h3 className={`text-lg font-semibold transition-colors ${theme === 'dark'
                                    ? 'text-white group-hover:text-blue-400'
                                    : 'text-text-primary group-hover:text-primary'
                                    }`}>
                                    {control.name}
                                </h3>
                                <p className={`text-sm mt-2 line-clamp-2 ${theme === 'dark' ? 'text-slate-400' : 'text-text-secondary'}`}>
                                    {control.description || 'No description provided.'}
                                </p>
                            </div>

                            <div className={`flex items-center justify-between pt-4 border-t ${theme === 'dark' ? 'border-slate-700/50' : 'border-border'}`}>
                                <div className={`flex gap-2 text-xs ${theme === 'dark' ? 'text-slate-500' : 'text-text-secondary'}`}>
                                    <div className="flex items-center gap-1">
                                        <Layers className="w-3 h-3" />
                                        <span>{control.bomIds.length} Linked Items</span>
                                    </div>
                                </div>

                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                        onClick={() => console.log('🔍 Filtering similar controls')}
                                        className={`p-1.5 rounded-md transition-colors ${theme === 'dark'
                                            ? 'hover:bg-slate-700 text-slate-400 hover:text-white'
                                            : 'hover:bg-background text-text-secondary hover:text-text-primary'
                                            }`}>
                                        <Filter className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => console.log('⚙️ Management Options')}
                                        className={`p-1.5 rounded-md transition-colors ${theme === 'dark'
                                            ? 'hover:bg-slate-700 text-slate-400 hover:text-white'
                                            : 'hover:bg-background text-text-secondary hover:text-text-primary'
                                            }`}>
                                        <MoreVertical className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Hover Glow Effect - Only in dark mode */}
                            {theme === 'dark' && (
                                <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-transparent via-blue-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                            )}
                        </div>
                    ))}
                </div>

            </div>
        </>
    );
};

// Subcomponents
const StatCard = ({ title, value, icon: Icon, color, theme }: any) => {
    const colorStyles: any = {
        blue: 'text-blue-400 bg-blue-500/10',
        emerald: 'text-emerald-400 bg-emerald-500/10',
        purple: 'text-purple-400 bg-purple-500/10',
    };

    return (
        <div className={`p-4 rounded-xl border flex items-center gap-4 ${theme === 'dark'
            ? 'border-slate-700/50 bg-slate-800/40 backdrop-blur-sm'
            : 'border-border bg-card'
            }`}>
            <div className={`p-3 rounded-lg ${colorStyles[color]}`}>
                <Icon className="w-6 h-6" />
            </div>
            <div>
                <p className={`text-sm font-medium ${theme === 'dark' ? 'text-slate-400' : 'text-text-secondary'}`}>{title}</p>
                <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-text-primary'}`}>{value}</p>
            </div>
        </div>
    );
};

const TabButton = ({ active, onClick, label, icon: Icon, theme }: any) => (
    <button
        onClick={onClick}
        className={`
      flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-all
      ${active
                ? theme === 'dark'
                    ? 'bg-slate-700 text-white shadow-sm'
                    : 'bg-primary text-white shadow-sm'
                : theme === 'dark'
                    ? 'text-slate-400 hover:text-white hover:bg-slate-800'
                    : 'text-text-secondary hover:text-text-primary hover:bg-background'
            }
    `}
    >
        {Icon && <Icon className="w-4 h-4" />}
        {label}
    </button>
);

export default ControlsManager;
